import { useState } from 'react';
import { Phone } from 'lucide-react';

import { Button } from '@/components/livekit/livekit/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useAgent } from '@/context/AgentContext';

interface WelcomeViewProps {
  startButtonText: string;
  onStartCall: (agentId: string, metadata: Record<string, string>) => void;
}

export const WelcomeView = ({
  startButtonText,
  onStartCall,
  ref,
}: React.ComponentProps<'div'> & WelcomeViewProps) => {
  const { agent } = useAgent();

  const [state, setState] = useState<Record<string, string>>({});

  return (
    <div ref={ref}>
      <section className="bg-background flex flex-col items-center justify-center text-center">
        <div className="flex flex-col items-center justify-center h-full space-y-6 px-4 pb-10">
          <div className="text-center space-y-2">
            <div className="relative w-20 h-20 mx-auto mb-4">
              {/* Ripple circles */}
              <div className="absolute inset-0 rounded-full border-2 border-primary/20 animate-ping"></div>
              <div
                className="absolute inset-0 rounded-full border-2 border-primary/15 animate-ping"
                style={{ animationDelay: '0.25s' }}
              ></div>
              <div
                className="absolute inset-0 rounded-full border-2 border-primary/10 animate-ping"
                style={{ animationDelay: '1s' }}
              ></div>

              {/* Main icon container */}
              <div className="relative w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                <Phone className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h3 className="text-lg font-medium">Start test call</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Click the button below to initiate a test call.
            </p>
          </div>
        </div>

        <form
          className="flex flex-col items-center justify-center w-full px-4 mb-10"
          onSubmit={e => {
            e.preventDefault();
            onStartCall(agent?.id || '', state);
          }}
        >
          <div className="space-y-4 w-full max-w-sm">
            {agent?.system_prompt?.variables?.map(varName => (
              <div key={varName} className="space-y-2">
                <Label htmlFor={`var-${varName}`} className="text-sm font-medium">
                  {varName}
                </Label>
                <Input
                  id={`var-${varName}`}
                  value={state[varName] || ''}
                  onChange={e =>
                    setState(prev => ({
                      ...prev,
                      [varName]: e.target.value,
                    }))
                  }
                  placeholder={`Enter ${varName}`}
                />
              </div>
            ))}
          </div>
          <div className="space-y-4 w-full max-w-sm">
            <Button
              type="submit"
              disabled={false}
              className="w-full mt-4"
              variant="primary"
              size="lg"
            >
              <Phone className="h-4 w-4" />
              {startButtonText}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
};
